import { endOfToday, format, startOfToday } from 'date-fns';
import {
  InvoiceFilterType,
  getFilterDisplayDates,
  InvoiceFilterOrderType,
  InvoiceQuickFilterOptions,
} from '@frontend/api-invoices';
import { useLocationGroupStore } from '@frontend/scope';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';

type InvoiceState = {
  currentPage: number;
  displayDates: [Date, Date] | [];
  filter: InvoiceFilterType;
  numRows: number;
  order: InvoiceFilterOrderType;
  workflowIds: string[];
  selectedStatus?: InvoiceQuickFilterOptions;
};

type InvoiceActions = {
  setCurrentPage: (page: InvoiceState['currentPage']) => void;
  resetCurrentPage: () => void;
  setFilter: (filter: InvoiceState['filter']) => void;
  setNumRows: (numRows: InvoiceState['numRows']) => void;
  setOrder: (order: InvoiceState['order']) => void;
  setWorkflowIds: (workflowIds: string[]) => void;
  setFilterDisplayDates: (dateRange: InvoiceFilterType['dateRange']) => void;
  setSelectedStatus: (selectedStatus: InvoiceQuickFilterOptions) => void;
};

type InvoiceStore = InvoiceState & InvoiceActions;

export const DEFAULT_INVOICE_LIST_ORDER = '-billedAt';

const DEFAULT_DISPLAY_DATE_FORMAT = 'MM/dd/yyyy';
const formatDisplayDate = (date: Date | string | number) => {
  return date && !isNaN(new Date(date).getTime()) ? format(new Date(date), DEFAULT_DISPLAY_DATE_FORMAT) : '';
};

const getDefaultFilterRange = () => ({
  start: formatDisplayDate(startOfToday()),
  end: formatDisplayDate(endOfToday()),
});

export const DEFAULT_INVOICE_SELECTED_STATUS = 'allInvoices' satisfies InvoiceQuickFilterOptions;

const initialFilter: InvoiceFilterType = {
  dateRange: getDefaultFilterRange(),
};

export const useInvoiceStore = createStoreWithSubscribe<InvoiceStore>(
  (set) => {
    const unsubscribeAppScope = useLocationGroupStore.subscribe(
      (state) => state.selectedLocationIds,
      () => {
        // Reset current page when location changes
        set((prevState) => ({
          ...prevState,
          currentPage: 1,
        }));
      }
    );
    return {
      filter: initialFilter,
      displayDates: getFilterDisplayDates(initialFilter.dateRange),
      selectedStatus: DEFAULT_INVOICE_SELECTED_STATUS,
      currentPage: 1,
      numRows: 10,
      order: '',
      workflowIds: [],

      setFilterDisplayDates: (dateRange: InvoiceFilterType['dateRange']) => {
        set((prevState) => {
          return {
            ...prevState,
            filter: {
              ...prevState.filter,
              dateRange,
              displayDates: getFilterDisplayDates(dateRange),
            },
          };
        });
      },
      setFilter: (filter: InvoiceFilterType) => {
        set((prevState) => ({
          ...prevState,
          filter,
          currentPage: 1,
          displayDates: getFilterDisplayDates(filter.dateRange),
        }));
      },

      setOrder: (order: InvoiceState['order']) => {
        set((prevState) => {
          return {
            ...prevState,
            order,
          };
        });
      },

      setWorkflowIds: (workflowIds) => set((prevState) => ({ ...prevState, workflowIds })),
      setCurrentPage: (page: number) => set((prevState) => ({ ...prevState, currentPage: page })),
      resetCurrentPage: () =>
        set((prevState) => ({ ...prevState, currentPage: 1, order: '' satisfies InvoiceFilterOrderType })),
      setNumRows: (numRows: number) => set((prevState) => ({ ...prevState, numRows, currentPage: 1 })),
      setSelectedStatus: (selectedStatus: InvoiceQuickFilterOptions) =>
        set((prevState) => ({ ...prevState, selectedStatus })),
      destroy: () => unsubscribeAppScope(),
    };
  },
  { name: 'InvoiceStore' }
);

export const useInvoiceShallowStore = createShallowStore(useInvoiceStore);
